<template>
  <div
    v-observe-visibility="{
      callback: visibilityChanged,
      once: true,
      throttle: 100,
      intersection: {
        rootMargin: '200px 0px'
      }
    }"
    class="no-page-break-inside"
  >
    <div
      v-if="!viewer_mode"
      class="py-1 px-2 fw-500 hover-gray d-md-flex justify-content-md-between align-items-center"
    >
      <div class="fw-500">
        <span v-if="chartData">
          <b-icon icon="bar-chart-line" scale="1" shift-v="1.25" aria-hidden="true" />
          {{ chartData?.label }}
        </span>
      </div>
      <div class="button-pair">
        <ChartBtn
          :edit_delete_mode="true"
          :selected_mng_chart_id="chart_id"
          :dc_key_info="dc_key_info"
          :sb_disclosure_id="sb_disclosure_id"
        />
        <b-button
          v-b-tooltip.hover
          variant="light"
          class="ml-1 py-1 px-2"
          title="없애기"
          @click.stop.prevent="removeChild"
        >
          <b-icon icon="trash" />
        </b-button>
      </div>
    </div>

    <div
      v-if="viewer_mode"
      :id="`chart-${chart_id}`"
      class="business-case-link cursor mb-2 fw-500 pt-4"
      :style="main_color"
      @click="toggleChartFromAccordion"
    >
      <b-icon icon="bar-chart-line" scale="1" shift-v="0" aria-hidden="true" />
      <span v-if="chartData">
        {{ lang != 'ENG' ? chartData?.label : chartData_e?.label }}
      </span>

      <div v-if="!print_mode" class="float-right mb-2">
        <b-button variant="outline-dark" size="sm mr-2 pr-2" @click="download_as_png">
          <b-icon icon="file-earmark-image" scale="1" shift-v="0" aria-hidden="true" />
          PNG
        </b-button>
        <b-button variant="outline-dark" size="sm pr-2" @click="download_as_pdf">
          <b-icon icon="file-earmark-pdf" scale="1" shift-v="0" aria-hidden="true" />
          PDF
        </b-button>
      </div>
    </div>

    <div
      v-if="lazy_load && !isVisible"
      class="chart-placeholder border border-light rounded mt-2 d-flex align-items-center justify-content-center"
      :style="'height: ' + (chartData?.options?.chart_height || 400) + 'px;'"
    >
      <b-spinner variant="secondary" label="Loading chart..." />
    </div>

    <canvas
      v-show="(isVisible || !lazy_load) && isRendered"
      :id="`chart_canvas_${chart_id}`"
      ref="qesgChart"
      :style="'max-height: ' + (chartData?.options?.chart_height || 400) + 'px;'"
      class="border border-light rounded mt-2"
    ></canvas>
    <p
      v-if="!!chartData?.options?.footnote || !!chartData_e?.options?.footnote"
      class="fw-400 f-90 mb-0 text-muted"
      style="white-space: pre-line"
    >
      <span v-if="lang === 'KOR'">
        {{ chartData?.options?.footnote }}
      </span>
      <span v-else>
        {{ chartData_e?.options?.footnote }}
      </span>
    </p>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';
import { mapState, mapGetters, mapMutations } from 'vuex';
import html2canvas from 'html2canvas';
import html2pdf from 'html2pdf.js';
import ChartBtn from '@/components/writing_report/chart_subs/ChartBtn';
import { resolveDatalabelsOptions, inferChartType } from '@/util/chart/datalabels.utils.js';
import { resolveAnnotationOptions } from '@/util/chart/annotation.utils.js';
import axios from 'axios';

export default {
  name: 'ChartCanvas',
  components: {
    ChartBtn
  },
  props: {
    payload: { type: Object, default: () => null },
    chart_id: { type: Number, default: () => null },
    viewer_mode: { type: Boolean, default: () => false },
    print_mode: { type: Boolean, default: () => false },
    lv2_info: { type: Object, default: () => null },
    lv3_idx: { type: Number, default: () => null },
    dc_key_info: { type: Array, default: () => [] },
    sb_disclosure_id: { type: Number, default: () => null },
    lazy_load: { type: Boolean, default: false }
  },
  computed: {
    ...mapState('report_list', ['report_code', 'lang']),
    ...mapGetters('report_list', ['selected_report']),
    ...mapState('charts', ['chart_data', 'edited_chart_id']),
    main_color() {
      return `color:` + this.selected_report.main_color;
    }
  },
  data() {
    return {
      // chart 인스턴스는 created()에서 non-reactive 로 선언 (Vue Observer 무한 재귀 방지)
      chartData: null,
      chartData_e: null,
      isVisible: false,
      isRendered: false,
      renderTimeout: null
    };
  },
  watch: {
    lang: {
      handler() {
        if (this.isVisible || !this.lazy_load) {
          // isRendered 플래그를 false로 설정하여 재렌더링 강제
          this.isRendered = false;
          this.debounceRenderChart();
        }
      }
    },
    print_mode: {
      handler() {
        if (this.isVisible || !this.lazy_load) {
          this.debounceRenderChart();
        }
      }
    },
    chart_data: {
      handler(newArray) {
        // 배열을 순회하면서 특정 object의 값이 변경되었는지 확인하고 처리
        if (this.edited_chart_id !== null && this.edited_chart_id === this.chart_id) {
          this.chartData = newArray.find(obj => obj['chart_id'] === this.chart_id)['form'];

          if (this.isVisible) {
            this.debounceRenderChart();
          }
          this.update_edited_chart_id(null);
        }
      },
      deep: true
    }
  },
  created() {
    // chart.js 인스턴스를 Vue reactive 영역(data())에 두면 내부 helpers/segment 참조가
    // Observer에 의해 무한 enumerate되어 RangeError 발생. instance property로 두어 회피.
    this.chart = null;
  },
  mounted() {
    if (this.payload !== null) {
      this.chartData = { ...this.payload };
    }
    // If lazy_load is disabled, show immediately
    if (!this.lazy_load) {
      this.isVisible = true;
      this.$nextTick(() => {
        this.debounceRenderChart();
      });
    }
  },
  beforeDestroy() {
    this.cleanupChart();
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
      this.renderTimeout = null;
    }
  },
  methods: {
    ...mapMutations('charts', ['update_edited_chart_id']),
    formatUnit(context) {
      const unitLabel = this.lang === 'KOR' ? '단위' : 'unit';
      return context.parsed.y !== undefined
        ? `${context.dataset.label}: ${context.parsed.y
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',')} (${unitLabel}: ${context.dataset?.unit})`
        : `${context.dataset.label}: ${context.parsed
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',')} (${unitLabel}: ${context.dataset?.unit})`;
    },

    visibilityChanged(isVisible) {
      if (isVisible && !this.isVisible) {
        this.isVisible = true;
        this.$nextTick(() => {
          this.debounceRenderChart();
        });
      }
    },

    debounceRenderChart() {
      if (this.renderTimeout) {
        clearTimeout(this.renderTimeout);
      }
      this.renderTimeout = setTimeout(() => {
        this.renderChart();
      }, 100);
    },

    cleanupChart() {
      if (this.chart) {
        try {
          this.chart.destroy();
        } catch (e) {
          console.warn('Chart destroy error:', e);
        }
        this.chart = null;
      }

      // Canvas 컨텍스트 명시적 정리 (Safari 메모리 최적화)
      if (this.$refs.qesgChart) {
        const canvas = this.$refs.qesgChart;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          // Safari에서 메모리 해제 강제
          canvas.width = 0;
          canvas.height = 0;
        }
      }
    },

    toggleChartFromAccordion(event) {
      const targetElement = event.currentTarget.nextElementSibling;
      if (targetElement) {
        targetElement.classList.toggle('hide');
      }
    },
    async renderChart() {
      if ((!this.isVisible && this.lazy_load) || !this.$refs.qesgChart) {
        console.log('Skipping render - conditions not met');
        return;
      }

      // 기존 차트 완전 정리
      this.cleanupChart();

      // 렌더링 전 메모리 정리를 위한 대기
      await new Promise(resolve => setTimeout(resolve, 50));

      try {
        // 언어 변경 시 필요한 데이터가 없으면 다시 로드
        const needsKoreanData = this.lang !== 'ENG' && this.chartData === null;
        const needsEnglishData = this.lang === 'ENG' && this.chartData_e === null;

        if (needsKoreanData || needsEnglishData || this.chartData === null) {
          if (this.print_mode) {
            const response = await this.$publicApi.get(`/getChartDataById?id=${this.chart_id}`);
            if (response.data.code) {
              this.chartData = response.data.result.contents;
              this.chartData_e = response.data.result.contents_em
                ? response.data.result.contents_em
                : response.data.result.contents_ea;
            }
          } else {
            let response = this.chart_data.find(obj => obj['chart_id'] === this.chart_id);
            if (!response) {
              response = await this.$publicApi.get(`/getChartDataById?id=${this.chart_id}`);
              if (response.data.code) {
                this.chartData = response.data.result.contents;
                this.chartData_e = response.data.result.contents_em
                  ? response.data.result.contents_em
                  : response.data.result.contents_ea;
              }
            } else {
              this.chartData = response['form'];
              this.chartData_e = response['form_em'] ? response['form_em'] : response['form_ea'];
            }
          }
        }

        // chart.js plugin(특히 annotation)이 옵션/datasets 내부에 chart reference를 박는 경우가
        // 있어 store의 reactive 원본을 그대로 넘기면 Observer 무한 재귀가 발생할 수 있다.
        // deep clone으로 store/Vue reactive와 완전히 분리한다.
        const _source = this.lang !== 'ENG' ? this.chartData : this.chartData_e;
        if (!_source || !_source.options) {
          console.warn('Chart options not available for language:', this.lang);
          return;
        }
        const chartOptions = JSON.parse(JSON.stringify(_source));

        // Safari 메모리 최적화를 위한 설정
        chartOptions.options = {
          ...chartOptions.options,
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 0 // Safari에서 애니메이션 비활성화로 메모리 절약
          },
          interaction: {
            intersect: false,
            mode: 'point'
          }
        };

        chartOptions.plugins = [
          {
            id: 'customCanvasBackgroundColor',
            beforeDraw: (chart, args, options) => {
              const { ctx } = chart;
              ctx.save();
              ctx.globalCompositeOperation = 'destination-over';
              ctx.fillStyle = options.color || 'white';
              ctx.fillRect(0, 0, chart.width, chart.height);
              ctx.restore();
            }
          },
          ChartDataLabels,
          annotationPlugin
        ];

        const _savedDatalabels = chartOptions.options?.plugins?.datalabels;
        const _savedAnnotations = chartOptions.options?.plugins?.annotations;
        const _chartType = inferChartType(chartOptions);
        chartOptions.options.plugins = {
          ...chartOptions.options.plugins,
          tooltip: {
            intersect: true,
            callbacks: {
              label: this.formatUnit
            }
          },
          datalabels: resolveDatalabelsOptions(_savedDatalabels, _chartType),
          annotation: resolveAnnotationOptions(_savedAnnotations, _chartType)
        };

        // Canvas 크기 제한 (Safari 메모리 최적화)
        const canvas = this.$refs.qesgChart;
        const maxWidth = Math.min(canvas.parentElement.clientWidth || 800, 1200);
        const maxHeight = Math.min(chartOptions.options.chart_height || 400, 600);

        canvas.style.maxWidth = maxWidth + 'px';
        canvas.style.maxHeight = maxHeight + 'px';

        if (!canvas) return;
        // 같은 canvas에 stale chart 인스턴스가 남아있으면 "Canvas is already in use" 에러가 나므로 강제 정리
        Chart.getChart(canvas)?.destroy();
        const ctx = canvas.getContext('2d');
        this.chart = new Chart(ctx, chartOptions);
        this.isRendered = true;
      } catch (error) {
        console.error('Chart render error:', error);
        this.isRendered = false;
      }
    },
    async download_as_png() {
      const chartElement = this.$refs.qesgChart;
      try {
        const canvas = await html2canvas(chartElement);
        const dataURL = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = dataURL;
        const file_name = this.lang !== 'ENG' ? this.chartData.label : this.chartData_e.label;
        link.download = file_name + '.png';
        link.click();
      } catch (error) {
        error.message = '[차트 다운로드]' + error.message;
        this.$Sentry.captureException(error);
      }
    },
    async download_as_pdf() {
      const canvas = this.$refs.qesgChart;
      const file_name = this.lang !== 'ENG' ? this.chartData.label : this.chartData_e.label;
      try {
        html2pdf()
          .from(canvas)
          .set({
            margin: 1,
            filename: file_name + '.pdf',
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, width: canvas.width * 1.4 },
            jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
          })
          .save();
      } catch (error) {
        error.message = '[차트 다운로드]' + error.message;
        this.$Sentry.captureException(error);
      }
    },
    removeChild() {
      if (this.sb_disclosure_id === null) {
        this.$emit('removeChild', this.lv2_info, this.lv3_idx);
      } else {
        this.$emit('delete_disclosure', this.sb_disclosure_id);
      }
    }
  }
};
</script>

<style scoped>
.chart-placeholder {
  background-color: #f8f9fa;
}

.chart-placeholder:hover {
  background-color: #e9ecef;
}

/* Safari 메모리 최적화를 위한 Canvas 스타일 */
canvas {
  image-rendering: auto;
  image-rendering: crisp-edges;
  image-rendering: pixelated;
}
</style>
